'use client'
import React, {
  ChangeEvent,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { useSetValueFromForm } from '@/hooks/Form'
import cn from 'classnames'

import { Icon } from '@/icons'

import styles from './Checkbox.module.scss'
import { FieldPath, FieldValues, UseFormRegister } from 'react-hook-form'

interface CheckboxProps<TFormData extends FieldValues> {
  value?: boolean
  onChange?: (value: boolean, name: string) => void
  className?: string
  label?: string
  name?: string
  error?: string
  disabled?: boolean
  testId?: string
  register?: UseFormRegister<TFormData>
}
export const Checkbox = <TFormData extends FieldValues>({
  label = '',
  value: outsideValue,
  onChange = () => {},
  error,
  name = '',
  children,
  className = '',
  disabled = false,
  testId = 'checkboxInput',
  register,
}: PropsWithChildren<CheckboxProps<TFormData>>) => {
  const [localValue, setLocalValue] = useState<boolean>(false)

  const change = useCallback(
    (newValue: boolean) => {
      if (outsideValue == null) {
        setLocalValue(newValue)
      }
      if (onChange != null) {
        onChange(newValue, name)
      }
    },
    [name, onChange, outsideValue]
  )

  useSetValueFromForm(name, change)

  const onChangeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      change(event.target.checked)
      if (register) {
        const registerData = register(name as FieldPath<TFormData>)
        if (registerData.onChange) {
          registerData.onChange(event)
        }
        setLocalValue(event.target.checked)
      }
    },
    [change, name, register]
  )

  const value = useMemo(
    () => outsideValue ?? localValue,
    [outsideValue, localValue]
  )

  return (
    <>
      <label
        className={cn(styles.container, className, {
          [styles.isDisabled]: disabled,
          [styles.isChecked]: value,
        })}
      >
        {register ? (
          <input
            className={styles.input}
            type="checkbox"
            data-testid={testId}
            {...register(name as FieldPath<TFormData>)}
            onChange={onChangeInput}
          />
        ) : (
          <input
            className={styles.input}
            type="checkbox"
            checked={disabled ? false : value}
            name={name}
            onChange={onChangeInput}
            data-testid={testId}
          />
        )}
        <span
          className={cn(styles.mark, {
            [styles.isChecked]: value,
            [styles.isError]: error != null && error.length !== 0,
          })}
        >
          <Icon
            name="checkMedium"
            className={cn(styles.icon, {
              [styles.isVisible]: value,
            })}
          />
        </span>
        {label ? <span className={styles.label}>{label}</span> : children}
      </label>
      {error != null && error.length !== 0 ? (
        <div className={styles.error}>{error}</div>
      ) : null}
    </>
  )
}
